/**
 * WB3-3 — OperationalThread model + step status machine
 * @see doc/system-completion-plan/01-target/BUSINESS_WORKBENCH_NORTH_STAR_V3.md §4
 */

import { resolveDomainActionFamily, type DomainActionFamily } from './domain-action-rule-table';
import type { ObjectRef } from './workbench-focus';
import type { WorkbenchIntent } from './workbench-intent';

export const CORRECTION_THREAD_STEP_ID = 'approval_correction' as const;
export const CORRECTION_APPLIED_THREAD_STEP_ID = 'correction_applied' as const;
export const CORRECTION_THREAD_BLOCKING_REASON = '승인 보정 검토가 필요합니다';

export type ThreadStepStatus = 'done' | 'active' | 'blocked' | 'pending';

export interface OperationalThreadStep {
  id: string;
  label: string;
  status: ThreadStepStatus;
  objectRef?: ObjectRef;
  requiredActionId?: string;
  blockingReason?: string;
}

export interface OperationalThread {
  id: string;
  title: string;
  rootObject: ObjectRef;
  currentObject?: ObjectRef;
  intent: WorkbenchIntent;
  steps: OperationalThreadStep[];
}

const VALID_STEP_TRANSITIONS: Readonly<Record<ThreadStepStatus, readonly ThreadStepStatus[]>> = {
  pending: ['active', 'blocked'],
  active: ['done', 'blocked', 'pending'],
  blocked: ['active', 'pending'],
  done: ['active'],
};

export function canTransitionStepStatus(from: ThreadStepStatus, to: ThreadStepStatus): boolean {
  if (from === to) {
    return true;
  }
  return VALID_STEP_TRANSITIONS[from].includes(to);
}

export function getActiveStep(thread: OperationalThread): OperationalThreadStep | undefined {
  return thread.steps.find((step) => step.status === 'active');
}

export function getBlockedSteps(thread: OperationalThread): OperationalThreadStep[] {
  return thread.steps.filter((step) => step.status === 'blocked');
}

export function getNextActionableStep(
  thread: OperationalThread
): OperationalThreadStep | undefined {
  const active = getActiveStep(thread);
  if (active) {
    return active;
  }
  return thread.steps.find((step) => step.status === 'blocked' || step.status === 'pending');
}

export interface ThreadNextAction {
  stepId: string;
  label: string;
  requiredActionId?: string;
  blockingReason?: string;
}

export function resolveThreadNextAction(thread: OperationalThread): ThreadNextAction | null {
  const blocked = getBlockedSteps(thread)[0];
  if (blocked) {
    return {
      stepId: blocked.id,
      label: blocked.label,
      ...(blocked.requiredActionId !== undefined
        ? { requiredActionId: blocked.requiredActionId }
        : {}),
      ...(blocked.blockingReason !== undefined ? { blockingReason: blocked.blockingReason } : {}),
    };
  }

  const active = getActiveStep(thread);
  if (active) {
    return {
      stepId: active.id,
      label: active.label,
      ...(active.requiredActionId !== undefined
        ? { requiredActionId: active.requiredActionId }
        : {}),
    };
  }

  const pending = thread.steps.find((step) => step.status === 'pending');
  if (pending) {
    return {
      stepId: pending.id,
      label: pending.label,
      ...(pending.requiredActionId !== undefined
        ? { requiredActionId: pending.requiredActionId }
        : {}),
    };
  }

  return null;
}

export function transitionStepStatus(
  steps: OperationalThreadStep[],
  stepId: string,
  nextStatus: ThreadStepStatus,
  patch?: Pick<OperationalThreadStep, 'blockingReason' | 'requiredActionId'>
): OperationalThreadStep[] {
  return steps.map((step) => {
    if (step.id !== stepId) {
      return step;
    }
    if (!canTransitionStepStatus(step.status, nextStatus)) {
      return step;
    }
    const next: OperationalThreadStep = { ...step, status: nextStatus };
    if (nextStatus === 'blocked' && patch?.blockingReason !== undefined) {
      next.blockingReason = patch.blockingReason;
    } else if (nextStatus !== 'blocked') {
      delete next.blockingReason;
    }
    if (patch?.requiredActionId !== undefined) {
      next.requiredActionId = patch.requiredActionId;
    }
    return next;
  });
}

export function patchOperationalThread(
  thread: OperationalThread,
  patch: Partial<Pick<OperationalThread, 'title' | 'intent' | 'steps'>>
): OperationalThread {
  return { ...thread, ...patch };
}

export function isObjectRefInThread(thread: OperationalThread, ref: ObjectRef): boolean {
  if (thread.rootObject.id === ref.id && thread.rootObject.businessDomain === ref.businessDomain) {
    return true;
  }
  return thread.steps.some(
    (step) => step.objectRef?.id === ref.id && step.objectRef?.businessDomain === ref.businessDomain
  );
}

export interface WorkspaceThreadStepTemplate {
  id: string;
  label: string;
  requiredActionId?: string;
}

export const WORKSPACE_THREAD_STEP_BLUEPRINTS: Record<
  DomainActionFamily,
  readonly WorkspaceThreadStepTemplate[]
> = {
  entity: [
    { id: 'review', label: '검토' },
    { id: 'maintain', label: '정보 갱신', requiredActionId: 'entity:update' },
    { id: 'complete', label: '완료', requiredActionId: 'review:complete' },
  ],
  finance: [
    { id: 'review', label: '전표 검토' },
    { id: 'post', label: '전기', requiredActionId: 'finance:post' },
    { id: 'close', label: '마감', requiredActionId: 'finance:close' },
  ],
  warehouse: [
    { id: 'review', label: '입고 검토' },
    { id: 'receive', label: '입고 처리', requiredActionId: 'warehouse:receive' },
    { id: 'complete', label: '완료', requiredActionId: 'warehouse:complete' },
  ],
  item: [
    { id: 'review', label: '품목 검토' },
    { id: 'maintain', label: '마스터 갱신', requiredActionId: 'item:update' },
    { id: 'complete', label: '완료', requiredActionId: 'review:complete' },
  ],
  inventory: [
    { id: 'review', label: '재고 확인' },
    { id: 'adjust', label: '조정', requiredActionId: 'inventory:adjust' },
    { id: 'complete', label: '완료', requiredActionId: 'inventory:complete' },
  ],
  manufacturing: [
    { id: 'review', label: '생산 검토' },
    { id: 'produce', label: '생산 실행', requiredActionId: 'manufacturing:produce' },
    { id: 'complete', label: '완료', requiredActionId: 'manufacturing:complete' },
  ],
  customer: [
    { id: 'review', label: '고객 검토' },
    { id: 'engage', label: '상호작용', requiredActionId: 'customer:engage' },
    { id: 'complete', label: '완료', requiredActionId: 'review:complete' },
  ],
  supplier: [
    { id: 'review', label: '공급업체 검토' },
    { id: 'engage', label: '협의', requiredActionId: 'supplier:engage' },
    { id: 'complete', label: '완료', requiredActionId: 'review:complete' },
  ],
  order: [
    { id: 'review', label: '주문 검토' },
    { id: 'process', label: '주문 처리', requiredActionId: 'order:process' },
    { id: 'fulfill', label: '이행', requiredActionId: 'order:fulfill' },
  ],
  collection: [
    { id: 'review', label: '컬렉션 검토' },
    { id: 'curate', label: '구성', requiredActionId: 'collection:curate' },
    { id: 'complete', label: '완료', requiredActionId: 'review:complete' },
  ],
  task: [
    { id: 'review', label: '업무 검토' },
    { id: 'assign', label: '배정', requiredActionId: 'start_assignment_workflow' },
    { id: 'complete', label: '완료', requiredActionId: 'review:complete' },
  ],
  reference: [
    { id: 'locate', label: '참조 확인' },
    { id: 'verify', label: '연관 검증', requiredActionId: 'reference:verify' },
    { id: 'complete', label: '완료', requiredActionId: 'review:complete' },
  ],
};

export const DEFAULT_WORKSPACE_THREAD_STEP_BLUEPRINT: readonly WorkspaceThreadStepTemplate[] = [
  { id: 'review', label: '검토' },
  { id: 'complete', label: '완료' },
];

export function resolveWorkspaceThreadStepBlueprint(
  businessDomain: string
): readonly WorkspaceThreadStepTemplate[] {
  const family = resolveDomainActionFamily(businessDomain);
  if (!family) {
    return DEFAULT_WORKSPACE_THREAD_STEP_BLUEPRINT;
  }
  return WORKSPACE_THREAD_STEP_BLUEPRINTS[family];
}

export function buildWorkspaceThreadSteps(
  rootObject: ObjectRef,
  businessDomain: string = rootObject.businessDomain
): OperationalThreadStep[] {
  const blueprint = resolveWorkspaceThreadStepBlueprint(businessDomain);
  return blueprint.map((template, index) => ({
    id: template.id,
    label: template.label,
    status: index === 0 ? 'active' : 'pending',
    ...(index === 0 ? { objectRef: rootObject } : {}),
    ...(template.requiredActionId !== undefined
      ? { requiredActionId: template.requiredActionId }
      : {}),
  }));
}

export interface WorkspaceContextThreadInput {
  rootObject: ObjectRef;
  intent: WorkbenchIntent;
  title: string;
  threadId?: string;
  surface?: 'detail' | 'list' | 'dashboard' | 'queue' | 'stream_execute';
}

/** P2-8 — domain-family workspace thread builder (replaces synthetic review/complete fallback). */
export function createThreadFromWorkspaceContext(
  input: WorkspaceContextThreadInput
): OperationalThread {
  const rootObject = input.rootObject;

  return {
    id: input.threadId?.trim() || `thread:workspace:${rootObject.businessDomain}:${rootObject.id}`,
    title: input.title.trim() || '워크벤치',
    rootObject,
    ...(input.surface === 'detail' ? { currentObject: rootObject } : {}),
    intent: input.intent,
    steps: buildWorkspaceThreadSteps(rootObject),
  };
}

/** TL-8 — flow definition editor/builder operational thread step ids */
export const FLOW_DEFINITION_STEP_IDS = {
  define: 'define',
  validate: 'validate',
  publish: 'publish',
} as const;

export type FlowDefinitionStepId =
  (typeof FLOW_DEFINITION_STEP_IDS)[keyof typeof FLOW_DEFINITION_STEP_IDS];

export const BASIC_FLOW_BUILDER_WIZARD_STEP_COUNT = 7;

export interface FlowDefinitionThreadInput {
  flowDefinitionId: string;
  flowLabel?: string;
  intent?: WorkbenchIntent;
  threadId?: string;
  activeStepId?: FlowDefinitionStepId;
}

const FLOW_DEFINITION_STEP_ORDER: readonly FlowDefinitionStepId[] = [
  FLOW_DEFINITION_STEP_IDS.define,
  FLOW_DEFINITION_STEP_IDS.validate,
  FLOW_DEFINITION_STEP_IDS.publish,
];

const FLOW_DEFINITION_STEP_LABELS: Record<FlowDefinitionStepId, string> = {
  [FLOW_DEFINITION_STEP_IDS.define]: '정의',
  [FLOW_DEFINITION_STEP_IDS.validate]: '검증',
  [FLOW_DEFINITION_STEP_IDS.publish]: '배포',
};

export function resolveBasicFlowBuilderActiveStepId(wizardStepIndex: number): FlowDefinitionStepId {
  return wizardStepIndex >= BASIC_FLOW_BUILDER_WIZARD_STEP_COUNT - 1
    ? FLOW_DEFINITION_STEP_IDS.validate
    : FLOW_DEFINITION_STEP_IDS.define;
}

/** TL-8 — maps flow definition editor/builder context to operational thread */
export function createThreadFromFlowDefinitionContext(
  input: FlowDefinitionThreadInput
): OperationalThread {
  const rootObject: ObjectRef = {
    id: input.flowDefinitionId,
    businessDomain: 'flow_definition',
    ...(input.flowLabel !== undefined ? { label: input.flowLabel } : {}),
  };
  const activeStepId = input.activeStepId ?? FLOW_DEFINITION_STEP_IDS.define;
  const activeIndex = FLOW_DEFINITION_STEP_ORDER.indexOf(activeStepId);

  return {
    id: input.threadId?.trim() || `thread:flow-definition:${input.flowDefinitionId}`,
    title: input.flowLabel?.trim() || '업무 흐름 정의',
    rootObject,
    intent: input.intent ?? 'audit_review',
    steps: FLOW_DEFINITION_STEP_ORDER.map((stepId, index) => ({
      id: stepId,
      label: FLOW_DEFINITION_STEP_LABELS[stepId],
      status: index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending',
      ...(stepId !== FLOW_DEFINITION_STEP_IDS.publish ? { objectRef: rootObject } : {}),
      ...(stepId === FLOW_DEFINITION_STEP_IDS.validate
        ? { requiredActionId: 'flow:validate' }
        : {}),
      ...(stepId === FLOW_DEFINITION_STEP_IDS.publish ? { requiredActionId: 'flow:publish' } : {}),
    })),
  };
}

export function isFlowDefinitionThread(thread: OperationalThread): boolean {
  return (
    thread.rootObject.businessDomain === 'flow_definition' &&
    thread.steps.some((step) => step.id === FLOW_DEFINITION_STEP_IDS.define)
  );
}

export interface FlowExecutionThreadInput {
  flowExecutionId: string;
  flowName?: string;
  stageName?: string;
  taskName?: string;
  targetEntityId?: string;
  targetBusinessDomain?: string;
  intent?: WorkbenchIntent;
  /** v1 — optional blocked step for stub demos */
  blockedReason?: string;
}

/** v1 stub — maps flow execution context to a minimal operational thread */
export function createThreadFromFlowExecution(input: FlowExecutionThreadInput): OperationalThread {
  const rootObject: ObjectRef = {
    id: input.targetEntityId?.trim() || input.flowExecutionId,
    businessDomain: input.targetBusinessDomain?.trim() || 'flow_execution',
    ...(input.flowName !== undefined ? { label: input.flowName } : {}),
  };

  const executeStatus: ThreadStepStatus = input.blockedReason ? 'blocked' : 'pending';

  return {
    id: `thread:flow:${input.flowExecutionId}`,
    title: input.flowName?.trim() || input.taskName?.trim() || 'Flow 처리',
    rootObject,
    intent: input.intent ?? 'complete_task',
    steps: [
      {
        id: 'review',
        label: '검토',
        status: 'active',
        objectRef: rootObject,
      },
      {
        id: 'execute',
        label: input.stageName?.trim() || '실행',
        status: executeStatus,
        objectRef: rootObject,
        ...(input.blockedReason ? { blockingReason: input.blockedReason } : {}),
        requiredActionId: 'task:execute',
      },
      {
        id: 'complete',
        label: '완료',
        status: 'pending',
        requiredActionId: 'task:complete',
      },
    ],
  };
}

/** TL-8 — stream execute surface operational thread step ids */
export const STREAM_EXECUTE_STEP_IDS = {
  review: 'review',
  execute: 'execute',
  complete: 'complete',
} as const;

export interface StreamExecuteThreadInput {
  streamId: string;
  streamLabel?: string;
  threadId?: string;
  intent?: WorkbenchIntent;
  executeBlockedReason?: string;
}

/** TL-8 — maps stream template execute context to operational thread (WB4.6 P3) */
export function createThreadFromStreamExecuteContext(
  input: StreamExecuteThreadInput
): OperationalThread {
  const rootObject: ObjectRef = {
    id: input.streamId,
    businessDomain: 'stream_execution',
    ...(input.streamLabel !== undefined ? { label: input.streamLabel } : {}),
  };

  const executeStatus: ThreadStepStatus = input.executeBlockedReason ? 'blocked' : 'pending';

  return {
    id: input.threadId?.trim() || `thread:stream:${input.streamId}`,
    title: input.streamLabel?.trim() || '업무 흐름 실행',
    rootObject,
    intent: input.intent ?? 'complete_task',
    steps: [
      {
        id: STREAM_EXECUTE_STEP_IDS.review,
        label: '검토',
        status: 'active',
        objectRef: rootObject,
      },
      {
        id: STREAM_EXECUTE_STEP_IDS.execute,
        label: '실행',
        status: executeStatus,
        objectRef: rootObject,
        requiredActionId: 'stream:execute',
        ...(input.executeBlockedReason ? { blockingReason: input.executeBlockedReason } : {}),
      },
      {
        id: STREAM_EXECUTE_STEP_IDS.complete,
        label: '완료',
        status: 'pending',
        requiredActionId: 'stream:complete',
      },
    ],
  };
}

export function isStreamExecuteThread(thread: OperationalThread): boolean {
  return (
    thread.rootObject.businessDomain === 'stream_execution' &&
    thread.steps.some((step) => step.id === STREAM_EXECUTE_STEP_IDS.execute)
  );
}

export function syncStreamExecuteThreadWithWorkspace(
  thread: OperationalThread,
  state: { root?: ObjectRef; surface?: string }
): OperationalThread {
  if (!isStreamExecuteThread(thread) || state.surface !== 'stream_execute') {
    return thread;
  }

  const streamLabel = state.root?.label?.trim();
  if (!streamLabel) {
    return thread;
  }

  const rootObject: ObjectRef = {
    ...thread.rootObject,
    label: streamLabel,
  };

  return {
    ...thread,
    title: streamLabel,
    rootObject,
    steps: thread.steps.map((step) =>
      step.objectRef?.businessDomain === 'stream_execution'
        ? { ...step, objectRef: rootObject }
        : step
    ),
  };
}

export interface OrderContextThreadInput {
  orderId: string;
  orderLabel?: string;
  customerId?: string;
  customerLabel?: string;
  itemId?: string;
  itemLabel?: string;
  intent?: WorkbenchIntent;
  blockedReason?: string;
}

/** v1 stub — Customer → Order → Item style order workflow thread */
export function createThreadFromOrderContext(input: OrderContextThreadInput): OperationalThread {
  const orderRef: ObjectRef = {
    id: input.orderId,
    businessDomain: 'order',
    ...(input.orderLabel !== undefined ? { label: input.orderLabel } : {}),
  };

  const steps: OperationalThreadStep[] = [];

  if (input.customerId) {
    steps.push({
      id: 'customer',
      label: '고객 확인',
      status: 'done',
      objectRef: {
        id: input.customerId,
        businessDomain: 'customer',
        ...(input.customerLabel !== undefined ? { label: input.customerLabel } : {}),
      },
    });
  }

  steps.push({
    id: 'order',
    label: '주문 처리',
    status: 'active',
    objectRef: orderRef,
    requiredActionId: 'order:process',
  });

  if (input.itemId) {
    steps.push({
      id: 'item',
      label: '품목 확인',
      status: input.blockedReason ? 'blocked' : 'pending',
      objectRef: {
        id: input.itemId,
        businessDomain: 'item',
        ...(input.itemLabel !== undefined ? { label: input.itemLabel } : {}),
      },
      ...(input.blockedReason ? { blockingReason: input.blockedReason } : {}),
    });
  }

  steps.push({
    id: 'fulfill',
    label: '이행',
    status: 'pending',
    requiredActionId: 'order:fulfill',
  });

  return {
    id: `thread:order:${input.orderId}`,
    title: input.orderLabel?.trim() || `Order ${input.orderId}`,
    rootObject: orderRef,
    intent: input.intent ?? 'process_new_order',
    steps,
  };
}

export const SELLABLE_REVIEW_STEP_IDS = {
  selectItem: 'select_item',
  verifyStatus: 'verify_status',
  executeWork: 'execute_work',
  reviewComplete: 'review_complete',
} as const;

export const SELLABLE_SELECT_BLOCKING_REASON = '항목을 먼저 선택하세요';

export interface SellableThreadInput {
  rootObject: ObjectRef;
  intent?: WorkbenchIntent;
  title?: string;
  threadId?: string;
  selectedObject?: ObjectRef;
}

/** sellable Golden Slice — 4-step review thread (P79-4) */
export function createThreadFromSellableContext(input: SellableThreadInput): OperationalThread {
  const rootObject = input.rootObject;
  const selectedObject = input.selectedObject;

  return {
    id: input.threadId?.trim() || `thread:sellable:${rootObject.id}`,
    title: input.title?.trim() || '판매 가능 품목 검토',
    rootObject,
    ...(selectedObject !== undefined ? { currentObject: selectedObject } : {}),
    intent: input.intent ?? 'general_browse',
    steps: [
      {
        id: SELLABLE_REVIEW_STEP_IDS.selectItem,
        label: '항목 선택',
        status: selectedObject ? 'done' : 'blocked',
        objectRef: rootObject,
        ...(selectedObject ? {} : { blockingReason: SELLABLE_SELECT_BLOCKING_REASON }),
      },
      {
        id: SELLABLE_REVIEW_STEP_IDS.verifyStatus,
        label: '상태 확인',
        status: selectedObject ? 'active' : 'pending',
        ...(selectedObject !== undefined ? { objectRef: selectedObject } : {}),
      },
      {
        id: SELLABLE_REVIEW_STEP_IDS.executeWork,
        label: '필요한 작업 실행',
        status: 'pending',
        requiredActionId: 'workflow:inventory',
      },
      {
        id: SELLABLE_REVIEW_STEP_IDS.reviewComplete,
        label: '검토 완료',
        status: 'pending',
        requiredActionId: 'review:complete',
      },
    ],
  };
}

export function isSellableReviewThread(thread: OperationalThread): boolean {
  return thread.steps.some((step) => step.id === SELLABLE_REVIEW_STEP_IDS.selectItem);
}

function stripBlockingReason(step: OperationalThreadStep): OperationalThreadStep {
  const { blockingReason: _blockingReason, ...rest } = step;
  return rest;
}

export function syncSellableThreadWithWorkspace(
  thread: OperationalThread,
  state: { selection: ObjectRef[]; exceptionId?: string }
): OperationalThread {
  if (!isSellableReviewThread(thread)) {
    return thread;
  }

  const selected = state.selection[state.selection.length - 1];
  const steps = thread.steps.map((step) => ({ ...step }));
  const selectIdx = steps.findIndex((step) => step.id === SELLABLE_REVIEW_STEP_IDS.selectItem);
  if (selectIdx < 0) {
    return thread;
  }
  const selectStep = steps[selectIdx];
  if (!selectStep) {
    return thread;
  }

  if (!selected) {
    const progressedPastSelect = steps.some(
      (step, index) => index > selectIdx && (step.status === 'done' || step.status === 'active')
    );
    if (!progressedPastSelect) {
      steps[selectIdx] = {
        ...selectStep,
        status: 'blocked',
        blockingReason: SELLABLE_SELECT_BLOCKING_REASON,
      };
      for (let index = selectIdx + 1; index < steps.length; index += 1) {
        const step = steps[index];
        if (step && step.status !== 'done') {
          steps[index] = { ...step, status: 'pending' };
        }
      }
    }
  } else {
    if (selectStep.status === 'blocked') {
      steps[selectIdx] = stripBlockingReason({
        ...selectStep,
        status: 'done',
        objectRef: selected,
      });
    }
    const hasActive = steps.some((step) => step.status === 'active');
    if (!hasActive) {
      const nextIdx = steps.findIndex(
        (step, index) => index > selectIdx && step.status === 'pending'
      );
      if (nextIdx >= 0 && steps[nextIdx]) {
        steps[nextIdx] = { ...steps[nextIdx], status: 'active', objectRef: selected };
      }
    }
  }

  return {
    ...thread,
    steps,
    intent: state.exceptionId ? 'investigate_exception' : thread.intent,
    ...(selected !== undefined ? { currentObject: selected } : {}),
  };
}

export function advanceOperationalThreadStep(
  thread: OperationalThread,
  stepId: string
): OperationalThread {
  const stepIndex = thread.steps.findIndex((step) => step.id === stepId);
  if (stepIndex < 0) {
    return thread;
  }

  const targetStep = thread.steps[stepIndex];
  if (
    targetStep?.status === 'blocked' &&
    targetStep.blockingReason === SELLABLE_SELECT_BLOCKING_REASON
  ) {
    return thread;
  }

  const nextSteps = thread.steps.map((step, index) => {
    if (index <= stepIndex) {
      return stripBlockingReason({ ...step, status: 'done' });
    }
    if (index === stepIndex + 1) {
      return stripBlockingReason({ ...step, status: 'active' });
    }
    return step;
  });

  const nextActive = nextSteps.find((step) => step.status === 'active');

  return {
    ...thread,
    steps: nextSteps,
    ...(nextActive?.objectRef !== undefined ? { currentObject: nextActive.objectRef } : {}),
  };
}

export function activateOperationalThreadStep(
  thread: OperationalThread,
  stepId: string
): OperationalThread {
  const stepIndex = thread.steps.findIndex((step) => step.id === stepId);
  if (stepIndex < 0) {
    return thread;
  }

  const nextSteps = thread.steps.map((step, index) => {
    if (index < stepIndex) {
      return stripBlockingReason({ ...step, status: 'done' });
    }
    if (index === stepIndex) {
      return stripBlockingReason({ ...step, status: 'active' });
    }
    if (step.status === 'active' || step.status === 'blocked') {
      return stripBlockingReason({ ...step, status: 'pending' });
    }
    return step;
  });

  const activeStep = nextSteps[stepIndex];

  return {
    ...thread,
    steps: nextSteps,
    ...(activeStep?.objectRef !== undefined ? { currentObject: activeStep.objectRef } : {}),
  };
}

/** WB4.7 pilot — block thread until approval correction is reviewed. */
export function applyCorrectionThreadBlock(
  thread: OperationalThread,
  input?: { blockingReason?: string; requiredActionId?: string }
): OperationalThread {
  const existing = thread.steps.find((step) => step.id === CORRECTION_THREAD_STEP_ID);
  if (existing) {
    return {
      ...thread,
      steps: thread.steps.map((step) =>
        step.id === CORRECTION_THREAD_STEP_ID
          ? {
              ...step,
              status: 'blocked',
              blockingReason: input?.blockingReason ?? CORRECTION_THREAD_BLOCKING_REASON,
              ...(input?.requiredActionId ? { requiredActionId: input.requiredActionId } : {}),
            }
          : step
      ),
    };
  }

  return {
    ...thread,
    steps: [
      ...thread.steps,
      {
        id: CORRECTION_THREAD_STEP_ID,
        label: '승인 보정',
        status: 'blocked',
        blockingReason: input?.blockingReason ?? CORRECTION_THREAD_BLOCKING_REASON,
        requiredActionId: input?.requiredActionId ?? 'workbench:request-correction',
      },
    ],
  };
}

export function hasCorrectionAppliedStep(thread: OperationalThread): boolean {
  return thread.steps.some(
    (step) => step.id === CORRECTION_APPLIED_THREAD_STEP_ID && step.status === 'done'
  );
}

/** Records correction_applied as a done step and removes the blocked approval_correction step. */
/** P5-08 — records a done downstream spawn step on the operational thread. */
export const DOWNSTREAM_SPAWN_THREAD_STEP_PREFIX = 'spawn' as const;

export interface DownstreamSpawnThreadStepInput {
  label: string;
  spawnKind: 'flow' | 'task' | 'document' | 'thread';
  flowRef?: string;
  taskRef?: string;
  documentRef?: string;
}

export function buildDownstreamSpawnThreadStepId(
  spawnKind: DownstreamSpawnThreadStepInput['spawnKind']
): string {
  return `${DOWNSTREAM_SPAWN_THREAD_STEP_PREFIX}:${spawnKind}`;
}

export function applyDownstreamSpawnThreadStep(
  thread: OperationalThread,
  input: DownstreamSpawnThreadStepInput
): OperationalThread {
  const stepId = buildDownstreamSpawnThreadStepId(input.spawnKind);
  const refId = input.flowRef ?? input.taskRef ?? input.documentRef;
  const spawnStep: OperationalThreadStep = {
    id: stepId,
    label: input.label,
    status: 'done',
    ...(refId
      ? {
          objectRef: {
            id: refId,
            businessDomain:
              input.spawnKind === 'flow'
                ? 'flow_execution'
                : input.spawnKind === 'document'
                  ? 'document'
                  : input.spawnKind === 'thread'
                    ? 'collection'
                    : 'task_execution',
          },
        }
      : {}),
  };
  const existing = thread.steps.find((step) => step.id === stepId);
  if (existing) {
    return {
      ...thread,
      steps: thread.steps.map((step) => (step.id === stepId ? spawnStep : step)),
    };
  }
  return {
    ...thread,
    steps: [...thread.steps, spawnStep],
  };
}

export function applyCorrectionAppliedThreadStep(thread: OperationalThread): OperationalThread {
  const withoutBlock: OperationalThread = {
    ...thread,
    steps: thread.steps.filter((step) => step.id !== CORRECTION_THREAD_STEP_ID),
  };
  const existing = withoutBlock.steps.find((step) => step.id === CORRECTION_APPLIED_THREAD_STEP_ID);
  if (existing) {
    return {
      ...withoutBlock,
      steps: withoutBlock.steps.map((step) =>
        step.id === CORRECTION_APPLIED_THREAD_STEP_ID ? { ...step, status: 'done' } : step
      ),
    };
  }
  return {
    ...withoutBlock,
    steps: [
      ...withoutBlock.steps,
      {
        id: CORRECTION_APPLIED_THREAD_STEP_ID,
        label: '보정 반영',
        status: 'done',
      },
    ],
  };
}

export function clearCorrectionThreadBlock(
  thread: OperationalThread,
  options?: { force?: boolean }
): OperationalThread {
  if (hasCorrectionThreadBlock(thread) && !hasCorrectionAppliedStep(thread) && !options?.force) {
    return thread;
  }
  return {
    ...thread,
    steps: thread.steps.filter(
      (step) =>
        step.id !== CORRECTION_THREAD_STEP_ID && step.id !== CORRECTION_APPLIED_THREAD_STEP_ID
    ),
  };
}

export function hasCorrectionThreadBlock(thread: OperationalThread): boolean {
  return thread.steps.some(
    (step) => step.id === CORRECTION_THREAD_STEP_ID && step.status === 'blocked'
  );
}
